import { Component, Injector, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { finalize } from 'rxjs/operators';
import { PermissionConstants } from 'src/app/core/constants/permission-constants.constant';
import { CodeConverterOptions } from 'src/app/core/models/options/code-converter-options';
import { StockSKUSearchRequest } from 'src/app/core/models/transport-operation/stock-sku-search-request';
import { CommonDataService } from 'src/app/core/services/common-data.service';
import { DropdownOptionsService } from 'src/app/core/services/dropdown-options.service';
import { isNullOrEmpty } from 'src/app/core/utils/common-functions';
import { StockMaintenanceService } from 'src/app/features/stock/service/stock-maintenance.service';
import { PoServiceService } from 'src/app/features/transport-operation/services/po-service.service';
import { DataTableComponent } from 'src/app/shared/components/data-table/data-table.component';
import { SessionBaseComponent } from 'src/app/shared/components/session.base.component';


@Component({
  selector: 'laas-stock-sku-search',
  templateUrl: './stock-sku-search.component.html',
  styleUrls: ['./stock-sku-search.component.scss']
})
export class StockSkuSearchComponent extends SessionBaseComponent implements OnInit {

  @ViewChild('headerTable') dataTable!: DataTableComponent;
  readonly permissionCode = PermissionConstants.ViewStockSKUSearch
  currentLang: string = this.laasSetting.language;

  searchFormGroup: FormGroup;
  isSearchFormOpen: boolean = true;
  searchParams: StockSKUSearchRequest = new StockSKUSearchRequest();
  facilityMappingOptions: any[] = [];
  facilityList: any[] = [];
  storerMappingOptions: any[] = [];
  outboundColumns: any[];
  inventoryColumns: any[];
  inboundColumns: any[];
  outboundSearchResults: any[] = [];
  inventorySearchResults: any[] = [];
  inboundSearchResults: any[] = [];
  DOStatusOptions: any[] = [];
  statusOptions: any[] = [];
  IQCFlagOptions: any[] = [];

  isOutboundDataTableOpen: boolean = false;
  isInventoryDataTableOpen: boolean = false;
  isInboundDataTableOpen: boolean = false;

  isOutboundDataTableLoading: boolean = false;
  isInventoryDataTableLoading: boolean = false;
  isInboundDataTableLoading: boolean = false;

  isOutboundDataTableOpenPage: boolean = false;
  isInventoryDataTableOpenPage: boolean = false;
  isInboundDataTableOpenPage: boolean = false;

  constructor(
    injector: Injector,
    private formBuilder: FormBuilder,
    private poServiceService: PoServiceService,
    private dropdownOptionService: DropdownOptionsService,
    private stockMaintenanceService: StockMaintenanceService,
    private commonDataService: CommonDataService,
  ) {
    super(injector);

    this.searchFormGroup = this.formBuilder.group({
      sku: [null, Validators.required],
      hubPlantId: [null, Validators.required],
      facility: [null, Validators.required],
      storer: [null, Validators.required]
    });

    this.outboundColumns = [
      { field: 'externOrderKey', header: 'StockSKUSearch.DataTable.OutboundDataTable.Data.ExternOrderKey', sort: true },
      { field: 'openQty', header: 'StockSKUSearch.DataTable.OutboundDataTable.Data.OpenQty', sort: true },
      { field: 'originalQty', header: 'StockSKUSearch.DataTable.OutboundDataTable.Data.OriginalQty', sort: true },
      { field: 'orderDocStatus', header: 'StockSKUSearch.DataTable.OutboundDataTable.Data.OrderDocStatus', sort: true },
      { field: 'etd', header: 'StockSKUSearch.DataTable.OutboundDataTable.Data.Etd', sort: true },
      { field: 'specLocType', header: 'StockSKUSearch.DataTable.OutboundDataTable.Data.SpecLocType', sort: true }
    ]

    this.inventoryColumns = [
      { field: 'specLocType', header: 'StockSKUSearch.DataTable.InventoryDataTable.Data.SpecLocType', sort: true },
      { field: 'qtyAvailable', header: 'StockSKUSearch.DataTable.InventoryDataTable.Data.QtyAvailable', sort: true },
      { field: 'qtyAllocated', header: 'StockSKUSearch.DataTable.InventoryDataTable.Data.QtyAllocated', sort: true },
      { field: 'qty', header: 'StockSKUSearch.DataTable.InventoryDataTable.Data.Qty', sort: true },
      { field: 'qtyPicked', header: 'StockSKUSearch.DataTable.InventoryDataTable.Data.QtyPicked', sort: true }
    ]

    this.inboundColumns = [
      { field: 'externReceiptKey', header: 'StockSKUSearch.DataTable.InboundDataTable.Data.ExternReceiptKey', sort: true },
      { field: 'qtyReceived', header: 'StockSKUSearch.DataTable.InboundDataTable.Data.QtyReceived', sort: true },
      { field: 'qtyExpected', header: 'StockSKUSearch.DataTable.InboundDataTable.Data.QtyExpected', sort: true },
      { field: 'arriveDate', header: 'StockSKUSearch.DataTable.InboundDataTable.Data.ArriveDate', sort: true },
      { field: 'receiptDocStatus', header: 'StockSKUSearch.DataTable.InboundDataTable.Data.ReceiptDocStatus', sort: true },
      { field: 'iqcFlag', header: 'StockSKUSearch.DataTable.InboundDataTable.Data.IqcFlag', sort: true }
    ]
  }

  async ngOnInit(): Promise<void> {
    await super.ngOnInit();

    this.poServiceService.getMappingFacilityOptions().subscribe({
      next: (result) =>{
        const seenCodes = new Set<string>();
        this.facilityList = result
          .filter((x: { optionDesc: string }) => {
            if (seenCodes.has(x.optionDesc)) return false;
            seenCodes.add(x.optionDesc);
            return true;
          })
          .map((x: { optionDesc: string; optionCode: string }) => ({
            label: x.optionDesc,
            value: x.optionDesc,
          }));
        this.searchFormGroup.get('facility')?.setValue(this.facilityList[0].value);
      }
    });

    this.dropdownOptionService.getDOStatusOptions()
    .subscribe({
      next: (options: any[]) => {
        this.DOStatusOptions = options.map(x => ({ label: x.optionDesc, value: x.optionCode }));
      }
    });

    this.dropdownOptionService.getIQCFlagOptions().subscribe({
      next: (options: any[]) => {
        this.IQCFlagOptions = options.map(x => ({label: x.optionDesc, value: x.optionCode}));
      }
    })

    this.commonDataService.getCodeConverter(this.laasSetting.language, 'DropdownList', 'advising')
    .subscribe({
      next: (results: CodeConverterOptions[]) => {
        this.statusOptions = results.map((x) => ({
          label: x.codeDesc,
          value: x.codeKey,
        }));

        this.translateService.get('Common.Message.ChooseOption').subscribe({
          next: (msg) => {
            this.statusOptions.unshift({
              label: msg,
              value: '',
            });
          },
        });
      },
    });

    this.storerMappingOptions = ['WISTRON_BBP'];
    this.searchFormGroup.get('storer')?.setValue(this.storerMappingOptions[0]);
  }

  searchBtnClick(): void {
    if(this.searchFormGroup.invalid){
      this.toastService.warn(this.translateService.instant('ReceiptFormQuery.Alert.CheckRequired'));
      return;
    }
    this.isOutboundDataTableLoading = true;
    this.isInventoryDataTableLoading = true;
    this.isInboundDataTableLoading = true;
    this.isOutboundDataTableOpen = true;
    this.isInventoryDataTableOpen = true;
    this.isInboundDataTableOpen = true;
    this.isSearchFormOpen = false;
    this.searchSKUData();
  }

  searchSKUData(): void {
    const request = {
      sku: this.searchFormGroup.value.sku,
      hubPlantId: this.searchFormGroup.value.hubPlantId,
      facility: this.searchFormGroup.value.facility,
      storer: this.searchFormGroup.value.storer,
    };
    this.stockMaintenanceService.searchSKU(request)
    .pipe(finalize(() => (
      this.isOutboundDataTableLoading = false,
      this.isInventoryDataTableLoading = false,
      this.isInboundDataTableLoading = false
    )))
    .subscribe({
      next: (response: any) => {
        if (!isNullOrEmpty(response.data)) {
          this.inventorySearchResults = response.data.inventoryData
          this.inboundSearchResults = response.data.inboundData
          this.outboundSearchResults = response.data.outboundData
        }
      },
      error: (response: any) => {
        isNullOrEmpty(response.error.errorMsg) == true ?
        this.toastService.error(this.translateService.instant('Common.Message.UnknowError')) :
        this.toastService.error(this.translateService.instant(response.error.errorMsg));
        this.inventorySearchResults = [];
        this.inboundSearchResults = [];
        this.outboundSearchResults = [];
        this.isSearchFormOpen = true;
      },
      complete: () => {}
    })
  }
}
